/**
 * Sentry error monitoring initialization and helpers.
 *
 * Client-side: call `initSentry()` once at app startup.
 * API routes: use `captureApiError()` to report + tag errors.
 */
import * as Sentry from '@sentry/react';

const DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;

let initialized = false;

/** Initialize Sentry for the browser client. Call once in __root.tsx. */
export function initSentry() {
  if (initialized || !DSN) return;
  initialized = true;

  Sentry.init({
    dsn: DSN,
    environment: import.meta.env.MODE,
    release: `fragments-cloud@${import.meta.env.VITE_APP_VERSION ?? '0.1.0'}`,
    tracesSampleRate: 0.2,
    beforeSend(event) {
      // Scrub API keys from payloads
      if (event.extra) {
        for (const key of Object.keys(event.extra)) {
          const val = event.extra[key];
          if (typeof val === 'string' && val.startsWith('fc_')) {
            event.extra[key] = `${val.slice(0, 11)}****`;
          }
        }
      }
      return event;
    },
  });
}

/** Add a breadcrumb for key lifecycle events. */
export function addBreadcrumb(
  category: string,
  message: string,
  data?: Record<string, unknown>,
) {
  if (!DSN) return;
  Sentry.addBreadcrumb({ category, message, data, level: 'info' });
}

/** Capture an error with optional extra context. */
export function captureError(error: unknown, extra?: Record<string, unknown>) {
  if (!DSN) {
    console.error('[sentry:disabled]', error, extra);
    return;
  }
  Sentry.captureException(error, { extra });
}

/**
 * Capture an API route error with structured context.
 * Falls back to console.error when Sentry is not configured.
 */
export function captureApiError(
  error: unknown,
  context: {
    endpoint: string;
    method: string;
    ip?: string;
    keyPrefix?: string;
    orgId?: string;
    projectId?: string;
    requestId?: string;
    traceId?: string;
  },
) {
  const message = error instanceof Error ? error.message : String(error);

  // Always log structured JSON for Vercel log ingestion
  console.error(
    JSON.stringify({
      level: 'error',
      message,
      ...(error instanceof Error ? { stack: error.stack } : {}),
      ...context,
      timestamp: new Date().toISOString(),
    }),
  );

  if (!DSN) return;

  Sentry.withScope((scope) => {
    scope.setTag('endpoint', context.endpoint);
    scope.setTag('method', context.method);
    if (context.orgId) scope.setTag('orgId', context.orgId);
    if (context.projectId) scope.setTag('projectId', context.projectId);
    if (context.requestId) scope.setTag('requestId', context.requestId);
    if (context.traceId) scope.setTag('traceId', context.traceId);
    if (context.keyPrefix) scope.setExtra('keyPrefix', context.keyPrefix);
    if (context.ip) scope.setExtra('ip', context.ip);
    Sentry.captureException(error);
  });
}

export { Sentry };
